import { useState, useEffect } from 'react';
import { useAuthStore } from '../hooks/useAuthStore';
import { Stethoscope, Save, Calendar, User, Clock, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL || '/api';

const SPECIALTIES = ['GP','cardiologist','dermatologist','psychiatrist','neurologist','oncologist','gastroenterologist','endocrinologist','rheumatologist','other'];
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function DoctorSetup() {
  const { user, token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');

  const [profile, setProfile] = useState({
    provider_number: '', prescriber_number: '', specialty: 'GP',
    subspecialty: '', qualifications: '', bio: '', consultation_fee: '',
    available_for_booking: 1, available_days: 'Mon,Tue,Wed,Thu,Fri',
    available_from: '09:00', available_to: '17:00'
  });

  const [selectedDays, setSelectedDays] = useState(['Mon','Tue','Wed','Thu','Fri']);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadDoctorProfile();
  }, []);

  const loadDoctorProfile = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/doctors/profile.php`, { headers }).then(r => r.json());
      if (res.success) {
        if (res.profile) {
          const p = res.profile;
          setProfile(prev => ({ ...prev, ...p }));
          if (p.available_days) setSelectedDays(p.available_days.split(','));
        }
        setAppointments(res.appointments || []);
      }
    } catch (err) {
      console.error('Failed to load doctor profile:', err);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const data = { ...profile, available_days: selectedDays.join(',') };
      await fetch(`${API}/doctors/profile.php`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      toast.success('Doctor profile saved!');
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const getStatusColor = (status) => {
    const map = { pending: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-blue-100 text-blue-800', in_progress: 'bg-purple-100 text-purple-800', completed: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' };
    return map[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Doctor Dashboard</h1>
          <p className="text-gray-500 mt-1">Welcome Dr. {user?.first_name || user?.name?.split(' ')[0]} — manage your profile and appointments</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow text-center border border-blue-100">
            <p className="text-2xl font-bold text-blue-600">{appointments.length}</p>
            <p className="text-xs text-gray-500 mt-1">Upcoming appointments</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow text-center border border-green-100">
            <p className="text-2xl font-bold text-green-600">{profile.specialty}</p>
            <p className="text-xs text-gray-500 mt-1">Specialty</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow text-center border border-purple-100">
            <p className="text-2xl font-bold text-purple-600">{selectedDays.length}</p>
            <p className="text-xs text-gray-500 mt-1">Available days/week</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow text-center border border-orange-100">
            <p className="text-2xl font-bold text-orange-600">${profile.consultation_fee || '0'}</p>
            <p className="text-xs text-gray-500 mt-1">Consultation fee</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="flex border-b">
            {[{ id: 'profile', label: '🩺 My Profile', icon: Stethoscope }, { id: 'appointments', label: '📅 Appointments', icon: Calendar }].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-4 text-sm font-medium transition-colors ${activeTab === tab.id ? 'border-b-2 border-blue-600 text-blue-700 bg-blue-50' : 'text-gray-600 hover:bg-gray-50'}`}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {activeTab === 'profile' && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-6">Doctor Profile & Availability</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Provider Number (Medicare)</label>
                    <input value={profile.provider_number || ''} onChange={e => setProfile({...profile, provider_number: e.target.value})}
                      placeholder="e.g. 2345678A" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prescriber Number</label>
                    <input value={profile.prescriber_number || ''} onChange={e => setProfile({...profile, prescriber_number: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Specialty</label>
                    <select value={profile.specialty || 'GP'} onChange={e => setProfile({...profile, specialty: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 capitalize">
                      {SPECIALTIES.map(s => <option key={s} value={s} className="capitalize">{s === 'GP' ? 'General Practitioner (GP)' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subspecialty</label>
                    <input value={profile.subspecialty || ''} onChange={e => setProfile({...profile, subspecialty: e.target.value})}
                      placeholder="e.g. Tropical Medicine" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Consultation Fee (AUD)</label>
                    <input type="number" min="0" value={profile.consultation_fee || ''} onChange={e => setProfile({...profile, consultation_fee: e.target.value})}
                      placeholder="0 for bulk billing" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Available for Booking</label>
                    <select value={profile.available_for_booking} onChange={e => setProfile({...profile, available_for_booking: parseInt(e.target.value)})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value={1}>✅ Yes — accepting patients</option>
                      <option value={0}>❌ No — not accepting bookings</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Qualifications</label>
                    <input value={profile.qualifications || ''} onChange={e => setProfile({...profile, qualifications: e.target.value})}
                      placeholder="e.g. MBBS, FRACGP, FACEM" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Professional Bio</label>
                    <textarea value={profile.bio || ''} onChange={e => setProfile({...profile, bio: e.target.value})}
                      rows={3} placeholder="Brief professional description shown to patients..."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>

                <h3 className="font-bold text-gray-900 mt-8 mb-3">📅 Availability Schedule</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Available Days</label>
                  <div className="flex space-x-2">
                    {DAYS.map(day => (
                      <button key={day} onClick={() => toggleDay(day)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-colors ${selectedDays.includes(day) ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'}`}>
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Available From</label>
                    <input type="time" value={profile.available_from || '09:00'} onChange={e => setProfile({...profile, available_from: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Available To</label>
                    <input type="time" value={profile.available_to || '17:00'} onChange={e => setProfile({...profile, available_to: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <button onClick={saveProfile} disabled={saving}
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-3 px-8 rounded-xl transition-colors">
                    <Save className="w-5 h-5" />
                    <span>{saving ? 'Saving...' : 'Save Profile'}</span>
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'appointments' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900">Upcoming Appointments</h2>
                  <Link to="/my-appointments" className="text-sm text-blue-600 hover:underline">View all →</Link>
                </div>
                {appointments.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Calendar className="w-16 h-16 mx-auto mb-4 opacity-20" />
                    <p className="font-medium">No upcoming appointments</p>
                    <p className="text-sm mt-2">Make sure your profile is set up and you're accepting bookings</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {appointments.map(appt => (
                      <div key={appt.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 transition-colors">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{appt.patient_first} {appt.patient_last}</p>
                            <p className="text-sm text-gray-500">{appt.patient_email}</p>
                            {appt.chief_complaint && <p className="text-sm text-gray-600 mt-1">Chief complaint: {appt.chief_complaint}</p>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">{new Date(appt.scheduled_at).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                          <p className="text-sm text-gray-500">{new Date(appt.scheduled_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</p>
                          <span className={`mt-1 inline-block text-xs px-2 py-0.5 rounded-full ${getStatusColor(appt.status)}`}>{appt.status}</span>
                          {appt.video_room_name && (
                            <Link to={`/video-chat?room=${appt.video_room_name}`} className="block mt-1 text-xs text-blue-600 hover:underline">
                              📹 Join video
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
